import { Paper, Stack, Box, Typography, IconButton, Tooltip, Skeleton, Alert, Button } from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';

const renderIcon = (iconName, props = {}) => {
  const Icon = (iconName && MuiIcons[iconName]) || MuiIcons.Dashboard;
  return <Icon {...props} />;
};

// Presentational shell shared by every dashboard widget. Owns the card chrome
// (header, accent, edit-mode controls, loading/error/empty states) so the
// individual widgets only render their content. Mobile-first: cards fill their
// grid cell and stack to a single column on small screens.
export default function DashboardWidgetCard({
  title,
  icon,
  accent = 'primary',
  link,
  loading = false,
  error = null,
  onRetry,
  empty = false,
  emptyText = 'Keine Daten für heute.',
  editMode = false,
  onHide,
  dragHandleProps,
  headerAction,
  footer,
  children,
}) {
  const navigate = useNavigate();

  return (
    <Paper
      elevation={0}
      sx={theme => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 1.75, sm: 2.25 },
        borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `4px solid ${(theme.palette[accent] || theme.palette.primary).main}`,
        textAlign: 'left',
        transition: 'box-shadow 120ms ease, transform 120ms ease',
        '&:hover': { boxShadow: editMode ? undefined : theme.shadows[3] },
      })}
    >
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.25, minHeight: 32 }}>
        {editMode && (
          <Box
            {...dragHandleProps}
            sx={{ display: 'flex', cursor: 'grab', color: 'text.secondary', touchAction: 'none' }}
            aria-label='Verschieben'
          >
            <DragIndicatorIcon fontSize='small' />
          </Box>
        )}
        <Box sx={{ display: 'flex', color: `${accent}.main` }}>
          {renderIcon(icon, { fontSize: 'small' })}
        </Box>
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, flexGrow: 1, lineHeight: 1.25, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          noWrap
          title={title}
        >
          {title}
        </Typography>
        {headerAction}
        {editMode ? (
          <Tooltip title='Ausblenden'>
            <IconButton size='small' onClick={onHide} aria-label='Widget ausblenden'>
              <VisibilityOffIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        ) : (
          link && (
            <Tooltip title='Öffnen'>
              <IconButton size='small' onClick={() => navigate(link)} aria-label='Ansicht öffnen'>
                <OpenInNewIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )
        )}
      </Stack>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 64 }}>
        {loading ? (
          <Stack spacing={1} sx={{ pt: 0.5 }}>
            <Skeleton variant='rounded' height={28} width='55%' />
            <Skeleton variant='text' height={18} width='80%' />
            <Skeleton variant='text' height={18} width='65%' />
          </Stack>
        ) : error ? (
          <Alert
            severity='error'
            sx={{ py: 0, px: 1, fontSize: '0.78rem', alignItems: 'center' }}
            action={
              onRetry && (
                <Button color='inherit' size='small' startIcon={<RefreshIcon />} onClick={onRetry}>
                  Neu laden
                </Button>
              )
            }
          >
            Daten konnten nicht geladen werden.
          </Alert>
        ) : empty ? (
          <Stack sx={{ flexGrow: 1 }} alignItems='flex-start' justifyContent='center'>
            <Typography variant='body2' color='text.secondary'>
              {emptyText}
            </Typography>
          </Stack>
        ) : (
          children
        )}
      </Box>

      {footer && !loading && !error && (
        <Box sx={{ mt: 1.25, pt: 1, borderTop: theme => `1px dashed ${theme.palette.divider}` }}>
          {footer}
        </Box>
      )}
    </Paper>
  );
}
